import React, { useState, useRef, useEffect } from "react";
import "flag-icons/css/flag-icons.min.css";
import { LangCode, useLanguage } from "../../i18n/LanguageContext";

interface Language {
  code: LangCode;
  shortLabel: string; // VN - VI
  name: string; // Tiếng Việt
  flagClass: string;
}

const LANGUAGES: Language[] = [
  { code: "vi", shortLabel: "VIE", name: "Tiếng Việt", flagClass: "fi fi-vn" },
  { code: "en", shortLabel: "ENG", name: "English", flagClass: "fi fi-us" },
  { code: "lo", shortLabel: "LAOS", name: "ພາສາລາວ", flagClass: "fi fi-la" },
];

interface LanguageSelectorProps {
  isMobile: boolean;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  isMobile,
}) => {
  const { lang, setLang } = useLanguage();

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<Language>(LANGUAGES[0]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Click ngoài để đóng dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (lang: Language) => {
    setLang(lang.code);
    setOpen(false);
    window.location.reload();
  };

  useEffect(() => {
    const found = LANGUAGES.find((l) => l.code === lang);
    if (found) setCurrent(found);
  }, [lang]);

  return (
    <div className="relative inline-block" ref={wrapperRef}>
      {isMobile ? (
        <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-haspopup="listbox"
            aria-expanded={open}
            className={`
              inline-flex items-center justify-center gap-2
              rounded-full border border-primary/15 bg-white
              font-semibold text-primary shadow-sm
              transition-all duration-200
              hover:border-primary/35 hover:bg-green-50
              focus:outline-none focus:ring-2 focus:ring-primary/20
              active:scale-[0.98]
              h-9 min-w-10 px-2.5
            `}
          >
          <span
            className={current.flagClass}
            style={{
              width: 22,
              height: 22,
              display: "inline-block",
            }}
          />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="
            inline-flex items-center gap-2 rounded-full
            border border-white/20 bg-white
            px-4 py-1 text-sm font-semibold text-primary
            backdrop-blur-sm transition hover:border-white/30
            focus:outline-none focus:ring-2 focus:ring-white/40
          "
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span
            className={current.flagClass}
            style={{ width: 20, height: 20, display: "inline-block" }}
          />
          <span>{current.shortLabel}</span>
          <svg
            className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 011.08 1.04l-4.25 4.25a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-44 rounded-xl bg-white py-2 shadow-lg ring-1 ring-black/5 z-50">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleSelect(lang)}
              className={`flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-teal-50 ${
                lang.code === current.code
                  ? "font-semibold text-teal-700"
                  : "text-slate-700"
              }`}
            >
              <span
                className={lang.flagClass}
                style={{
                  width: 20,
                  height: 20,
                  display: "inline-block",
                }}
              />
              <div className="flex flex-col items-start">
                <span>{lang.name}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
